import {
  AlertTriangle,
  Ban,
  CalendarCheck,
  Check,
  CheckCircle2,
  Circle,
  Clock,
  Copy,
  Gauge,
  Handshake,
  HelpCircle,
  Link as LinkIcon,
  Loader,
  Mail,
  MapPin,
  MessageSquare,
  Minus,
  Pencil,
  Phone,
  Printer,
  Search,
  Shield,
  Sparkles,
  Square,
  ArrowUpRight,
  Table,
  FileText,
  LayoutList,
  Upload,
  User,
  Users,
  Video,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  ActivityTypes,
  AppointmentFormats,
  AppointmentStatuses,
  CampaignStatuses,
  Channels,
  CompanyStatuses,
  Confidences,
  ContactTypes,
  HandlingStatuses,
  JobStatuses,
  MessageStatuses,
  ReplyClasses,
  SecurityRiskLevels,
  SendResults,
  SkipReasons,
  SourceMethods,
  SuppressionTypes,
  TargetStates,
  UserStatuses,
  type StatusMeta,
} from '@nesc/shared';
import { cn } from '@/lib/utils';

/** アイコン名は docs/09-design-system.md 第4章の対応に従う */
const ICONS: Record<string, LucideIcon> = {
  circle: Circle,
  'arrow-up-right': ArrowUpRight,
  'message-square': MessageSquare,
  clock: Clock,
  'calendar-check': CalendarCheck,
  handshake: Handshake,
  'check-circle': CheckCircle2,
  'x-circle': XCircle,
  ban: Ban,
  check: Check,
  x: X,
  'help-circle': HelpCircle,
  'alert-triangle': AlertTriangle,
  loader: Loader,
  link: LinkIcon,
  'file-text': FileText,
  sparkles: Sparkles,
  square: Square,
  mail: Mail,
  phone: Phone,
  'layout-list': LayoutList,
  printer: Printer,
  'map-pin': MapPin,
  minus: Minus,
  pencil: Pencil,
  search: Search,
  table: Table,
  upload: Upload,
  user: User,
  users: Users,
  shield: Shield,
  video: Video,
  copy: Copy,
  gauge: Gauge,
};

export type StatusKind =
  | 'company_status'
  | 'send_result'
  | 'target_state'
  | 'campaign_status'
  | 'message_status'
  | 'reply_class'
  | 'handling_status'
  | 'appointment_status'
  | 'appointment_format'
  | 'job_status'
  | 'confidence'
  | 'contact_type'
  | 'channel'
  | 'source_method'
  | 'suppression_type'
  | 'user_status'
  | 'activity_type'
  | 'skip_reason'
  | 'security_risk';

const REGISTRY: Record<StatusKind, { meta: (v: string) => StatusMeta<string> }> = {
  company_status: CompanyStatuses,
  send_result: SendResults,
  target_state: TargetStates,
  campaign_status: CampaignStatuses,
  message_status: MessageStatuses,
  reply_class: ReplyClasses,
  handling_status: HandlingStatuses,
  appointment_status: AppointmentStatuses,
  appointment_format: AppointmentFormats,
  job_status: JobStatuses,
  confidence: Confidences,
  contact_type: ContactTypes,
  channel: Channels,
  source_method: SourceMethods,
  suppression_type: SuppressionTypes,
  user_status: UserStatuses,
  activity_type: ActivityTypes,
  skip_reason: SkipReasons,
  security_risk: SecurityRiskLevels,
};

/** 破線＝進行中・未確定の状態 */
const PENDING_VALUES = new Set([
  'scheduling',
  'sending',
  'pending',
  'holding',
  'queued',
  'running',
  'draft',
  'in_progress',
  'unknown',
  'invited',
  'postponed',
  'scheduled',
]);

/** 塗り＝完了状態（受注・アポ確定など） */
const SOLID_VALUES = new Set(['won', 'appointment', 'success', 'succeeded', 'held', 'completed']);

/** 強調＝結果不明・要対応・未対応 */
const EMPHASIS_VALUES = new Set(['unknown', 'needs_action', 'open', 'failed', 'excluded', 'bounce']);

export type StatusBadgeProps = {
  kind: StatusKind;
  value: string | null | undefined;
  /** 日付・理由などの補足。`・` 区切りで併記する */
  detail?: string | null;
  emphasis?: 'default' | 'high';
  /** カード内では 28px */
  size?: 'default' | 'lg';
  className?: string;
};

/**
 * 状態表示（docs/09-design-system.md 5.4）。
 * 状態名は必ず表示し、省略しない。P02：状態表示は必ずこの部品を使う。
 */
export const StatusBadge = ({
  kind,
  value,
  detail,
  emphasis,
  size = 'default',
  className,
}: StatusBadgeProps) => {
  if (!value) {
    return <span className={cn('text-body text-foreground-subtle', className)}>—</span>;
  }

  const meta = REGISTRY[kind].meta(value);
  const Icon = ICONS[meta.icon] ?? Circle;
  const isPending = PENDING_VALUES.has(value);
  const isSolid = SOLID_VALUES.has(value);
  const isEmphasized = emphasis === 'high' || (emphasis !== 'default' && EMPHASIS_VALUES.has(value));

  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-control px-2 text-note',
        size === 'lg' ? 'h-7' : 'h-6',
        isSolid
          ? 'bg-primary text-primary-foreground'
          : cn(
              'bg-background text-foreground-muted',
              isPending ? 'border border-dashed' : 'border',
              isEmphasized ? 'border-2 border-foreground font-semibold text-foreground' : 'border-border-strong',
            ),
        className,
      )}
      title={detail ? `${meta.label}・${detail}` : meta.description}
    >
      <Icon aria-hidden className={cn('size-4 shrink-0', value === 'running' && 'animate-spin')} />
      <span className="truncate">
        {meta.label}
        {detail && <span className="font-normal">・{detail}</span>}
      </span>
    </span>
  );
};

/** 状態のアイコンのみ（一覧の密度が高い場所） */
export const StatusIcon = ({
  kind,
  value,
  className,
}: {
  kind: StatusKind;
  value: string;
  className?: string;
}) => {
  const meta = REGISTRY[kind].meta(value);
  const Icon = ICONS[meta.icon] ?? Circle;
  return <Icon aria-label={meta.label} className={cn('size-4', className)} />;
};

export const statusLabel = (kind: StatusKind, value: string | null | undefined): string =>
  value ? REGISTRY[kind].meta(value).label : '—';
